import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Radio, ArrowRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApi, useQ, errMsg, fmtDuration } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Empty, LiveBadge } from "@/components/common";

const Pick = ({ label, value, onChange, items, testId }) => (
  <div className="space-y-2">
    <Label className="eyebrow">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const toIso = (local) => (local ? new Date(local).toISOString() : null);

const BroadcastPick = ({ channel, value, onChange }) => {
  const { t } = useT();
  const { data = [], error } = useQ(`/channels/${channel.id}/broadcasts`);
  if (error) return <p className="text-xs text-rose-300">{errMsg(error)}</p>;
  const items = [{ value: "none", label: t("no_broadcast") }, ...data.filter((b) => b.status !== "complete").map((b) => ({ value: b.id, label: `${b.title} · ${b.status}` }))];
  return <Pick label={t("yt_broadcast")} value={value} onChange={onChange} items={items} testId="stream-broadcast-select" />;
};

const StreamForm = ({ onDone }) => {
  const { t } = useT();
  const api = useApi();
  const { data: agents = [] } = useQ("/agents");
  const { data: channels = [] } = useQ("/channels");
  const [f, setF] = useState({ title: "", agent_id: "", channel_id: "none", broadcast_id: "none", mode: "qa", topic: "", scheduled_at: "", ends_at: "", auto_viewers: false, auto_viewers_interval: 30 });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const ytChannel = channels.find((c) => c.id === f.channel_id && c.connection === "youtube");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.agent_id) return toast.error(t("pick_agent"));
    try {
      await api.post("/streams", {
        ...f, channel_id: f.channel_id === "none" ? null : f.channel_id,
        broadcast_id: ytChannel && f.broadcast_id !== "none" ? f.broadcast_id : null,
        scheduled_at: f.mode === "scheduled" ? toIso(f.scheduled_at) : null, ends_at: toIso(f.ends_at),
      });
      toast.success(t("stream_created"));
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="stream-form">
      <div className="space-y-2"><Label className="eyebrow">{t("title")}</Label><Input required value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="stream-title-input" /></div>
      <Pick label={t("agent")} value={f.agent_id} onChange={(v) => set("agent_id", v)} items={agents.map((a) => ({ value: a.id, label: a.name }))} testId="stream-agent-select" />
      <Pick label={t("channel")} value={f.channel_id} onChange={(v) => setF((x) => ({ ...x, channel_id: v, broadcast_id: "none" }))} items={[{ value: "none", label: t("no_channel") }, ...channels.map((c) => ({ value: c.id, label: `${c.name}${c.connection === "youtube" ? " · YouTube" : ""}` }))]} testId="stream-channel-select" />
      {ytChannel && <BroadcastPick channel={ytChannel} value={f.broadcast_id} onChange={(v) => set("broadcast_id", v)} />}
      <Pick label={t("mode")} value={f.mode} onChange={(v) => set("mode", v)} items={["presenter", "qa", "scheduled"].map((m) => ({ value: m, label: t(`mode_${m}`) }))} testId="stream-mode-select" />
      <div className="space-y-2"><Label className="eyebrow">{t("topic")}</Label><Input value={f.topic} onChange={(e) => set("topic", e.target.value)} placeholder={t("topic_ph")} data-testid="stream-topic-input" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {f.mode === "scheduled" && <div className="space-y-2"><Label className="eyebrow">{t("scheduled_at")}</Label><Input type="datetime-local" required value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="stream-schedule-input" /></div>}
        <div className="space-y-2"><Label className="eyebrow">{t("ends_at")}</Label><Input type="datetime-local" value={f.ends_at} onChange={(e) => set("ends_at", e.target.value)} data-testid="stream-ends-input" /></div>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
        <div><p className="text-sm font-medium">{t("auto_viewers")}</p><p className="text-xs text-slate-500">{t("auto_viewers_hint")}</p></div>
        <Switch checked={f.auto_viewers} onCheckedChange={(v) => set("auto_viewers", v)} data-testid="stream-auto-viewers-switch" />
      </div>
      <Button type="submit" className="w-full rounded-full bg-indigo-600 hover:bg-indigo-500" data-testid="stream-create-btn">{t("create_stream")}</Button>
    </form>
  );
};

export default function Streams() {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { data: streams = [] } = useQ("/streams", { refetchInterval: 10000 });
  const [open, setOpen] = useState(false);
  const remove = async (id) => { try { await api.delete(`/streams/${id}`); qc.invalidateQueries({ queryKey: ["/streams"] }); } catch (e) { toast.error(errMsg(e)); } };
  const newBtn = <Button onClick={() => setOpen(true)} className="rounded-full bg-indigo-600 hover:bg-indigo-500" data-testid="new-stream-btn"><Plus className="h-4 w-4 mr-1" />{t("new_stream")}</Button>;

  return (
    <div>
      <PageHeader eyebrow={t("nav_streams")} title={t("streams_title")} subtitle={t("streams_sub")} action={newBtn} />
      {streams.length === 0 ? <Empty icon={Radio} title={t("no_streams")} text={t("no_streams_hint")} action={newBtn} testId="streams-empty" /> : (
        <div className="space-y-3">
          {streams.map((s) => (
            <Panel key={s.id} className={`flex flex-col sm:flex-row sm:items-center gap-4 !p-5 ${s.status === "live" ? "beam" : ""}`} data-testid={`stream-row-${s.id}`}>
              <LiveBadge live={s.status === "live"} />
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg font-bold truncate">{s.title}</p>
                <p className="text-xs text-slate-500">{t(`mode_${s.mode}`)} · {t(`status_${s.status}`)} · <span className="font-mono-ui">{fmtDuration(s.elapsed_seconds)}</span>
                  {s.schedule_state === "pending" && s.scheduled_at && <span className="text-indigo-300" data-testid={`stream-scheduled-${s.id}`}> · {t("starts")} {new Date(s.scheduled_at).toLocaleString()}</span>}
                  {s.ends_at && s.status !== "ended" && <span className="text-slate-400"> · {t("ends")} {new Date(s.ends_at).toLocaleString()}</span>}
                  {s.schedule_state === "failed" && <span className="text-rose-400"> · {t("schedule_failed")}</span>}
                  {s.broadcast_id && <span className="text-rose-300"> · YouTube</span>}
                  {s.auto_viewers && <span className="text-emerald-400"> · {t("auto_viewers")}</span>}
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm" className="bg-white text-zinc-950 hover:bg-indigo-100" data-testid={`stream-open-${s.id}`}><Link to={`/dashboard/streams/${s.id}`}>{t("open_room")} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
                {s.status !== "live" && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => remove(s.id)} data-testid={`stream-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </Panel>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111118] border-white/10">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("new_stream")}</DialogTitle></DialogHeader>
          <StreamForm onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["/streams"] }); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
